import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { TimelineEvent } from 'react-event-timeline';
import { FaPencilAlt, FaTrashAlt, FaTags } from 'react-icons/fa';
import lowercaseKeys from 'lowercase-keys';
import moment from 'moment';
import { MdForwardToInbox, MdReply, MdReplyAll } from 'react-icons/md';
import intlShape from '@/shared/utils/intlShape';
import DatePicker from '@/shared/components/input/DatePicker';
import { CanUpdate, CanCreate, CanRead, CanDelete } from '@/shared/components/security';
import Button from '@/shared/components/Button';
import TimeInput from '@/shared/components/input/TimeInput';
import RichTextEditor from '@/shared/components/RichTextEditor';
import classNames from 'classnames';
import dompurify from 'dompurify';
import AuthService from '@/shared/services/AuthService';
import {
  EmptyBookmarkIcon,
  BookmarkIcon,
  PhoneIcon,
  NoteIcon,
  AppointmentIcon,
  MailIcon,
  EnvelopeIcon,
  UserCircleIcon,
  AddIcon,
  ShareIcon,
  PdfIcon,
  UrlIcon,
  FolderOpenIcon,
  MagicIcon,
} from '@/shared/components/Icons';
import RelationName from '@/shared/components/RelationName';
import InlineAttachment from '@/shared/components/InlineAttachment';
import FileUpload from '@/shared/components/input/FileUpload';
import FormattedListValue from '@/shared/components/FormattedListValue';
import FormattedDateTime from '@/shared/components/FormattedDateTime';
import { isBeforeMinimum } from '@/shared/utils/validationHelpers';
import messages from './interactionMessages';
import TaskStatusToggle from '../tasks/TaskStatusToggle';

import InteractionTagMultiSelect from './InteractionTagMultiSelect';
import InteractionIcon from './InteractionIcon';
import config from '@/config';

const sanitizer = dompurify.sanitize;

class InteractionEvent extends Component {
  state = {};

  getInteractionBorderColor = interactionType => {
    switch (interactionType) {
      case 0:
        return '#757575';
      case 1:
        return '#6fba1c';
      case 2:
        return '#03a9f4';
      default:
        return '#757575';
    }
  };

  parseEmailMetaData = () => {
    const { interaction } = this.props;
    if (interaction.interactionType !== 2 || !interaction.metaDataJson) return null;

    let metadata = JSON.parse(interaction.metaDataJson);

    // old email interactions dont have additional metadata like to/cc/bcc/status, they're just an array of senders.
    // therefore, if this is the case, only render these senders without additional metadata
    const onlySenders = Array.isArray(metadata);
    if (!onlySenders) {
      metadata = lowercaseKeys(metadata);
    }
    return (
      <React.Fragment>
        {onlySenders ? (
          <div>
            <span className="bold">
              <FormattedMessage {...messages.From} />:
            </span>{' '}
            {metadata.join(';')}
          </div>
        ) : (
          <React.Fragment>
            <div>
              <span className="bold">
                <FormattedMessage {...messages.From} />:
              </span>{' '}
              {metadata.from}
            </div>
            <div className="margin5T">
              <span className="bold">
                <FormattedMessage {...messages.ToRecipient} />:
              </span>{' '}
              {metadata.to}
            </div>
            {metadata.cc && (
              <div className="margin5T">
                <span className="bold">
                  <FormattedMessage {...messages.Cc} />:
                </span>{' '}
                {metadata.cc}
              </div>
            )}
            {metadata.bcc && (
              <div>
                <span className="bold margin5T">
                  <FormattedMessage {...messages.Bcc} />:
                </span>{' '}
                {metadata.bcc}
              </div>
            )}
            {metadata.status > 0 && (
              <div className="margin5T">
                <span className="bold">
                  <FormattedMessage {...messages.Status} />:
                </span>{' '}
                <span className="text-nowrap">
                  <span className="fa-md">
                    {metadata.status === 2 && <i className="fa fa-info text-info green" />}
                    {metadata.status === 3 && <i className="fa fa-eye text-info green" />}
                    {metadata.status === 4 && <i className="fa fa-info text-info red" />}
                  </span>
                  <span className="margin5L">
                    <FormattedListValue list="emailstatus" id={metadata.status} />
                  </span>
                </span>
              </div>
            )}
            {metadata.reason && (
              <div>
                <span className="bold margin5T">
                  <FormattedMessage {...messages.Reason} />:
                </span>{' '}
                {metadata.reason}
              </div>
            )}
          </React.Fragment>
        )}
        <div className="margin5T">
          <span className="bold">
            <FormattedMessage {...messages.Subject} />:
          </span>{' '}
          {interaction.title}
        </div>
      </React.Fragment>
    );
  };

  render() {
    const {
      intl,
      interaction,
      onEditInteraction,
      onInteractionChanged,
      onSaveInteraction,
      onDeleteInteraction,
      onEditCancel,
      onPinInteraction,
      onRemovePin,
      onOpenEmail,
      modifiedBy,
      showRelationName,
      stateService,
      onDeleteAttachment,
      documentUploadEndpoint,
      onDocumentUploaded,
      onCreateTodo,
      onShareItem,
      onForwardEmail,
      onReplyToEmail,
      onExportToPdf,
      onSendViaEmail,
      onCopyLinkToInteraction,
      linkedTodos,
      showTaskDetailsModal,
      handleTasksUpdated,
      allowEdit,
      onSelectDocumentsFromAdminPulse,
      onGenerateTodos,
      generatingAI,
      onSummarizeInteraction,
    } = this.props;
    const { dateFocused } = this.state;
    const momentDate = interaction.date ? moment(interaction.date) : undefined;

    const isEmail = interaction.interactionType === 2;

    // add the interactionId to the generic document upload endpoint
    const endpoint = `${documentUploadEndpoint}&linkId=${interaction.id}&documentLinkType=6`;

    const hasHtml =
      (interaction.description && interaction.description.includes('html>')) || isEmail;

    const functionality =
      interaction.createdBy === AuthService.getUserId() ? 'InteractionsOwn' : 'InteractionsOthers';

    const canViewDocuments = AuthService.hasPermission({
      functionality: 'documents',
      privilege: 'read',
    });

    const hasAIFeature = AuthService.hasFeature('AI');

    return (
      <TimelineEvent
        title=""
        container="card"
        style={{ boxShadow: 'none', borderRadius: '0.6rem', borderBottom: '1px solid #d6d6d6' }}
        cardHeaderStyle={
          interaction.isPinned
            ? {
                backgroundColor: '#fef9ed',
                color: '#1f1646',
                padding: '1.6rem 1.6rem 0.8rem 1.6rem',
              }
            : {
                backgroundColor: 'transparent',
                color: '#1f1646',
                padding: '1.6rem 1.6rem 0.8rem 1.6rem',
              }
        }
        contentStyle={
          interaction.isPinned
            ? {
                backgroundColor: '#fef9ed',
                width: '100%',
                boxShadow: '0 none',
                padding: '0.8rem 1.6rem 3.2rem 1.6rem',
                marginBottom: '0',
              }
            : {
                backgroundColor: 'transparent',
                width: '100%',
                boxShadow: '0 none',
                marginBottom: '0',
                padding: '0.8rem 1.6rem 3.2rem 1.6rem',
              }
        }
        createdAt={
          interaction.editFocus ? (
            interaction.attachments &&
            interaction.attachments.map(attachment => (
              <InlineAttachment
                className="margin5T"
                key={attachment.id}
                attachment={attachment}
                onDelete={deletedAttachment =>
                  onDeleteAttachment(interaction.id, deletedAttachment)
                }
              />
            ))
          ) : (
            <span>
              <div>
                <span>
                  <FormattedDateTime value={interaction.date} />

                  {showRelationName && (
                    <span
                      className="clickable"
                      onClick={() => stateService.gotoRelation(interaction.relationId)}
                      title={intl.formatMessage(messages.GoToRelation)}
                    >
                      &nbsp;-&nbsp;
                      <RelationName
                        relation={{
                          name: interaction.relationName,
                          type: interaction.relationType,
                          alias: interaction.relationAlias,
                          crm: interaction.relationCrm,
                          id: interaction.relationId,
                        }}
                      />
                    </span>
                  )}
                </span>
                <span className="margin5L">
                  {interaction.attachments &&
                    interaction.attachments.map(attachment => (
                      <InlineAttachment
                        key={attachment.id}
                        attachment={attachment}
                        onDelete={deletedAttachment =>
                          onDeleteAttachment(interaction.id, deletedAttachment)
                        }
                      />
                    ))}
                </span>
              </div>
            </span>
          )
        }
        iconColor={this.getInteractionBorderColor(interaction.interactionType)}
        icon={<InteractionIcon intl={intl} type={interaction.interactionType} />}
        bubbleStyle={{ left: '2px' }}
      >
        <div className="interaction-event">
          {!interaction.editFocus && interaction.isPinned && (
            <CanUpdate on={functionality}>
              <div className="interaction-event-pins">
                <span
                  // style={
                  //   interaction.title
                  //     ? { position: 'relative', top: '-11px' }
                  //     : { position: 'relative', top: '-20px' }
                  // }
                  title={intl.formatMessage(messages.RemovePin)}
                  onClick={onRemovePin}
                  className="pinned"
                >
                  <BookmarkIcon />
                </span>
              </div>
            </CanUpdate>
          )}

          {!interaction.editFocus && !interaction.isPinned && (
            <CanUpdate on={functionality}>
              <div className="interaction-event-pins">
                <span
                  // style={
                  //   interaction.title
                  //     ? { position: 'relative', top: '-11px' }
                  //     : { position: 'relative', top: '-20px' }
                  // }
                  title={intl.formatMessage(messages.Pin)}
                  onClick={onPinInteraction}
                  className="unpinned"
                >
                  <BookmarkIcon />
                </span>
              </div>
            </CanUpdate>
          )}

          <div className={classNames({ 'pre-wrap': !hasHtml })}>
            {interaction.editFocus && (
              <div>
                <div style={{ display: 'flex' }}>
                  <DatePicker
                    placeholder={intl.formatMessage(messages.Date)}
                    date={momentDate}
                    onDateChange={value => onInteractionChanged(interaction.id, { date: value })}
                    focused={dateFocused}
                    onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                    id="interactionDate"
                    required
                    small
                    isOutsideRange={isBeforeMinimum}
                  />
                  <div className="margin5L" style={{ width: '135px' }}>
                    <TimeInput
                      value={interaction.minutes}
                      id="interactionMinutes"
                      onChange={value => onInteractionChanged(interaction.id, { minutes: value })}
                    />
                  </div>
                </div>
                <CanCreate on="documents">
                  <div>
                    <FileUpload
                      endpoint={endpoint}
                      onFileUploaded={(error, file) => onDocumentUploaded(error, file, interaction)}
                      removeSuccessfullAfter={5000}
                    />
                    <Button
                      btnClassName="btn tbn-default btn-block margin10T"
                      onClick={() => onSelectDocumentsFromAdminPulse()}
                    >
                      <FolderOpenIcon className="margin5R" />
                      <FormattedMessage {...messages.ChooseDocumentInAdminPulse} />
                    </Button>
                  </div>
                </CanCreate>
                <div className="form-group">
                  <label>
                    <FormattedMessage {...messages.Title} />
                  </label>
                  <input
                    type="text"
                    className="form-control margin10B"
                    value={interaction.title}
                    maxLength={500}
                    onChange={e => onInteractionChanged(interaction.id, { title: e.target.value })}
                    placeholder={intl.formatMessage(messages.Title)}
                  />
                </div>
                {interaction.summary && (
                  <div className="form-group">
                    <label>
                      <FormattedMessage {...messages.Summary} />
                    </label>
                    <RichTextEditor
                      initialValue={interaction.originalSummary || interaction.summary}
                      onChange={newContent =>
                        onInteractionChanged(interaction.id, { summary: newContent })
                      }
                      base64Images
                      id={`${interaction.id}-summary`}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>
                    <FormattedMessage {...messages.Description} />
                  </label>

                  <RichTextEditor
                    initialValue={interaction.originalDescription}
                    onChange={newContent =>
                      onInteractionChanged(interaction.id, { description: newContent })
                    }
                    base64Images
                    id={interaction.id}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FormattedMessage {...messages.Tags} />
                  </label>
                  <InteractionTagMultiSelect
                    value={interaction.tags}
                    onChange={tags => onInteractionChanged(interaction.id, { tags })}
                    allowNew
                    idProp="label"
                    caseSensitive={false}
                  />
                </div>

                <Button
                  btnClassName="btn btn-inverse btn-sm"
                  showProgress
                  onClick={onSaveInteraction}
                >
                  {intl.formatMessage(messages.Save)}
                </Button>
                <Button btnClassName="btn btn-default btn-sm margin5L" onClick={onEditCancel}>
                  {intl.formatMessage(messages.Cancel)}
                </Button>
              </div>
            )}
            {!interaction.editFocus && (
              <div>
                {!isEmail && <p className="interaction-event-title">{interaction.title}</p>}

                {!isEmail && interaction.summary && (
                  <div>
                    <div dangerouslySetInnerHTML={{ __html: sanitizer(interaction.summary) }} />
                    <hr className="margin10T margin10B" />
                  </div>
                )}

                {!isEmail && (
                  <div dangerouslySetInnerHTML={{ __html: sanitizer(interaction.description) }} />
                )}
                {!isEmail && linkedTodos && linkedTodos.length > 0 && (
                  <div>
                    <span className="bold">
                      <FormattedMessage {...messages.ToDos} />:
                    </span>
                    {linkedTodos.map(todo => (
                      <div key={todo.id} className="flex-row-nowrap">
                        <TaskStatusToggle
                          id={`TaskStatusToggle--${todo.id}`}
                          value={{
                            id: todo.id,
                            status: todo.status,
                            isBlocked: todo.isBlocked,
                            inapplicable: todo.inapplicable,
                            rejected: todo.rejected,
                            taskLevel: todo.taskLevel,
                          }}
                          isMainTask
                          onTasksUpdated={handleTasksUpdated}
                          onOpenDetail={todoId => showTaskDetailsModal(todoId)}
                        />
                        <span className="text-nowrap flex-item v-bottom">
                          <a
                            onClick={() => showTaskDetailsModal(todo.id)}
                            title={intl.formatMessage(messages.OpenDetails)}
                          >
                            {todo.name}
                          </a>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {!isEmail && allowEdit && (
                  <div className="margin5T">
                    <CanCreate on="Todo">
                      <Button onClick={onCreateTodo} btnClassName="btn-xs">
                        <AddIcon className="margin5R" />
                        <FormattedMessage {...messages.CreateNewToDo} />
                      </Button>
                    </CanCreate>
                    {hasAIFeature && (
                      <CanCreate on="Todo">
                        <Button
                          onClick={onGenerateTodos}
                          btnClassName="btn-xs margin10L"
                          disabled={generatingAI}
                          showProgress={generatingAI}
                        >
                          <MagicIcon className="margin5R" />
                          <FormattedMessage {...messages.GenerateToDo} />
                        </Button>
                      </CanCreate>
                    )}
                    {hasAIFeature && (
                      <Button
                        onClick={onSummarizeInteraction}
                        btnClassName="btn-xs margin10L"
                        disabled={generatingAI}
                        showProgress={generatingAI}
                      >
                        <MagicIcon className="margin5R" />
                        <FormattedMessage {...messages.SummarizeWithAI} />
                      </Button>
                    )}
                  </div>
                )}
                {isEmail && (
                  <div>
                    {/* Dont show senders if no metadata present (old emails) */}
                    {interaction.metaDataJson && this.parseEmailMetaData()}
                    {linkedTodos && linkedTodos.length > 0 && (
                      <div className="margin5T">
                        <span className="bold">
                          <FormattedMessage {...messages.ToDos} />:
                        </span>
                        {linkedTodos.map(todo => (
                          <div key={todo.id} className="flex-row-nowrap">
                            <TaskStatusToggle
                              id={`TaskStatusToggle--${todo.id}`}
                              value={{
                                id: todo.id,
                                status: todo.status,
                                isBlocked: todo.isBlocked,
                                inapplicable: todo.inapplicable,
                                rejected: todo.rejected,
                                taskLevel: todo.taskLevel,
                              }}
                              isMainTask
                              onTasksUpdated={handleTasksUpdated}
                              onOpenDetail={() => showTaskDetailsModal(todo.id)}
                            />
                            <span className="text-nowrap overflow-ellipsis flex-item">
                              <a
                                onClick={() => showTaskDetailsModal(todo.id)}
                                title={intl.formatMessage(messages.OpenDetails)}
                              >
                                {todo.name}
                              </a>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="margin5T">
                      <CanRead on="documents">
                        <Button
                          onClick={onOpenEmail}
                          btnClassName="btn-xs margin5R"
                          style={{ marginLeft: '-2px' }}
                        >
                          <EnvelopeIcon className="margin5R" />
                          <FormattedMessage {...messages.OpenEmail} />
                        </Button>
                      </CanRead>
                      <CanUpdate on="documents">
                        <Button
                          onClick={() => onReplyToEmail(false)}
                          btnClassName="btn-xs margin5R"
                          style={{ marginLeft: '-2px' }}
                        >
                          <MdReply className="margin5R react-icon" />
                          <FormattedMessage {...messages.Reply} />
                        </Button>
                        <Button
                          onClick={() => onReplyToEmail(true)}
                          btnClassName="btn-xs margin5R"
                          style={{ marginLeft: '-2px' }}
                        >
                          <MdReplyAll className="margin5R react-icon" />
                          <FormattedMessage {...messages.ReplyAll} />
                        </Button>
                        <Button
                          onClick={onForwardEmail}
                          btnClassName="btn-xs margin5R"
                          style={{ marginLeft: '-2px' }}
                        >
                          <MdForwardToInbox className="margin5R react-icon" />
                          <FormattedMessage {...messages.Forward} />
                        </Button>
                      </CanUpdate>
                      <CanCreate on="documents">
                        <CanCreate on="Todo">
                          <Button onClick={onCreateTodo} btnClassName="btn-xs">
                            <AddIcon className="margin5R" />
                            <FormattedMessage {...messages.CreateNewToDo} />
                          </Button>
                        </CanCreate>
                      </CanCreate>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {!interaction.editFocus && (
            <div className="interaction-event-actions">
              {interaction.tags && interaction.tags.length > 0 && (
                <span className="interaction-event-tags margin10R">
                  <FaTags />
                  &nbsp;
                  {interaction.tags.join(', ')}
                </span>
              )}
              {modifiedBy && !isEmail && (
                <span
                  className="margin10R"
                  title={intl.formatMessage(messages.LastModifiedBy, { name: modifiedBy })}
                  style={{
                    visibility: 'visible',
                    fontSize: '0.9em',
                  }}
                >
                  <span style={{ verticalAlign: 'middle', marginRight: '2px' }}>
                    <UserCircleIcon />
                  </span>

                  <span>{modifiedBy}</span>
                </span>
              )}
              {!isEmail && (
                <CanUpdate on={functionality}>
                  <span
                    title={intl.formatMessage(messages.Edit)}
                    onClick={onEditInteraction}
                    style={{ visibility: 'visible' }}
                  >
                    <FaPencilAlt className="margin10L react-icon" />
                  </span>
                </CanUpdate>
              )}
              {!isEmail || canViewDocuments ? (
                <>
                  <span
                    style={{ visibility: 'visible' }}
                    title={intl.formatMessage(messages.ExportToPdf)}
                    className="margin10L react-icon"
                    onClick={onExportToPdf}
                  >
                    {interaction.exportingToPdf && <i className="fa fa-spin fa-spinner" />}
                    {!interaction.exportingToPdf && (
                      <PdfIcon disabled={interaction.exportingToPdf} />
                    )}
                  </span>
                  <span
                    style={{ visibility: 'visible' }}
                    title={intl.formatMessage(messages.CopyLinkToInteraction)}
                    className="margin10L react-icon"
                    onClick={onCopyLinkToInteraction}
                  >
                    <UrlIcon />
                  </span>
                  <span
                    style={{ visibility: 'visible' }}
                    title={intl.formatMessage(messages.SendInPdfViaEmail)}
                    className="margin10L react-icon"
                    onClick={onSendViaEmail}
                  >
                    {interaction.sendingViaEmail && <i className="fa fa-spin fa-spinner" />}
                    {!interaction.sendingViaEmail && (
                      <EnvelopeIcon disabled={interaction.exportingToPdf} />
                    )}
                  </span>
                </>
              ) : null}
              <span
                style={{ visibility: 'visible' }}
                title={intl.formatMessage(messages.Share)}
                className="margin10L react-icon"
                onClick={onShareItem}
              >
                <ShareIcon />
              </span>
              <CanDelete on={functionality}>
                <span
                  style={{ visibility: 'visible' }}
                  title={intl.formatMessage(messages.Delete)}
                  className="margin10L react-icon"
                  onClick={onDeleteInteraction}
                >
                  <FaTrashAlt />
                </span>
              </CanDelete>
            </div>
          )}
        </div>
      </TimelineEvent>
    );
  }
}

InteractionEvent.propTypes = {
  intl: intlShape.isRequired,
};
InteractionEvent.defaultProps = {};

export default injectIntl(InteractionEvent);
